import { useState } from 'react'
import { Hospital, Room } from '../types'
import useSWR, { mutate } from 'swr'
import { fetcher, apiRequest } from '../utils/api'

function RoomManager() {
  const [showForm, setShowForm] = useState(false)
  const [editingRoom, setEditingRoom] = useState<Room | null>(null)
  const [formData, setFormData] = useState({ hospital_id: '', label: '', floor: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: rooms, error: roomsError } = useSWR<Room[]>('/rooms', fetcher)
  const { data: hospitals } = useSWR<Hospital[]>('/hospitals', fetcher)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const url = editingRoom ? `/rooms/${editingRoom.id}` : '/rooms'
      const method = editingRoom ? 'PATCH' : 'POST'
      
      await apiRequest(url, {
        method,
        body: JSON.stringify(formData),
      })

      mutate('/rooms')
      setFormData({ hospital_id: '', label: '', floor: '' })
      setShowForm(false)
      setEditingRoom(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save room')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (room: Room) => {
    setEditingRoom(room)
    setFormData({ hospital_id: room.hospital_id, label: room.label, floor: room.floor })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this room?')) return

    try {
      await apiRequest(`/rooms/${id}`, {
        method: 'DELETE',
      })

      mutate('/rooms')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete room')
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingRoom(null)
    setFormData({ hospital_id: '', label: '', floor: '' })
    setError(null)
  }

  const generateQRUrl = (roomId: string) => {
    return `${window.location.origin}/room/${roomId}`
  }

  if (roomsError) {
    return <div className="alert alert-error">Failed to load rooms</div>
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Room Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add Room
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
          <h3>{editingRoom ? 'Edit Room' : 'Add New Room'}</h3>
          
          <div className="form-group">
            <label>Hospital</label>
            <select
              value={formData.hospital_id}
              onChange={(e) => setFormData({ ...formData, hospital_id: e.target.value })}
              required
            >
              <option value="">Select a hospital</option>
              {hospitals?.map((hospital) => (
                <option key={hospital.id} value={hospital.id}>
                  {hospital.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Room Label</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="e.g., 101, A-205"
              required
            />
          </div>

          <div className="form-group">
            <label>Floor</label>
            <input
              type="text"
              value={formData.floor}
              onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
              placeholder="e.g., 1, 2, Ground"
              required
            />
          </div>

          <div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : editingRoom ? 'Update' : 'Add'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} style={{ marginLeft: '1rem' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Hospital</th>
            <th>Room</th>
            <th>Floor</th>
            <th>QR Code URL</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rooms?.map((room) => (
            <tr key={room.id}>
              <td>{room.hospital_name}</td>
              <td>{room.label}</td>
              <td>{room.floor}</td>
              <td>
                <a href={generateQRUrl(room.id)} target="_blank" rel="noopener noreferrer">
                  View QR Page
                </a>
                <br />
                <a href={`/api/qr/room/${room.id}`} target="_blank" rel="noopener noreferrer">
                  QR Code (PNG)
                </a>
              </td>
              <td>{new Date(room.created_at).toLocaleDateString()}</td>
              <td>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleEdit(room)}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDelete(room.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!rooms?.length && (
        <p>No rooms found. Add one to get started.</p>
      )}
    </div>
  )
}

export default RoomManager